import { useEffect, useState } from 'react';
import { cn } from '@/utils/utils';

const SPLASH = 'bg-[linear-gradient(90deg,#83f7ff_0%,#79e5cb_60%,#38d6e1_90%,var(--lureo-emphasis)_100%)]'; // theme-exempt: Lureo's loading gradient on the editor's splash, the brand's own aqua and mint into the theme's navy

// Shuffled once per page load, so each load reads differently and a remount doesn't reshuffle mid-load
const LOADING_TEXT_MESSAGES = [
  'Casting the characters...',
  'Animating the animations...',
  'Locating the locations...',
  'Anchoring the anchors...',
  'Modelling the models...',
  'Propping up the props...',
  'Masking the masks...',
  'Texturing the textures...',
  'Rigging the rigs...',
  'Plotting the plots...',
  'Syncing the sounds...',
  'Rendering the renders...',
  'Scripting the scripts...',
  'Layering the layers...',
  'Framing the frames...',
  'Timing the timelines...',
  'Compositing the composites...',
  'Colouring the colours...',
  'Mapping the maps...'
].sort(() => Math.random() - 0.5);

/** The editor's loading bar: fills on its own while loading, or shows `progress` (out of `maxValue`) when its caller sets it. */
export const ProgressBar = ({ maxValue, isFinishedLoading, progress }: { maxValue: number; isFinishedLoading: boolean; progress?: number }) => {
  // One step every 50ms while loading; the text changes every 20 steps
  const [steps, setSteps] = useState(0);
  const [wasFinished, setWasFinished] = useState(isFinishedLoading);
  if (wasFinished !== isFinishedLoading) {
    setWasFinished(isFinishedLoading);
    if (!isFinishedLoading) setSteps(0);
  }

  useEffect(() => {
    if (isFinishedLoading || progress !== undefined) return;
    const interval = setInterval(() => setSteps(prev => prev + 1), 50);
    return () => clearInterval(interval);
  }, [isFinishedLoading, progress]);

  const shown = progress ?? (isFinishedLoading ? maxValue : steps);
  const fraction = Math.min(shown / maxValue, 1);
  const loadingText = LOADING_TEXT_MESSAGES[Math.floor(shown / 20) % LOADING_TEXT_MESSAGES.length];

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      {/* Lureo's aqua-to-navy bar straight on the splash, with no track of its own */}
      <div role="meter" aria-label="Loading the editor" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(fraction * 100)} className="relative h-5 w-64 rounded-lureo-radius-pill">
        <div className={cn('absolute inset-y-0 left-0 corner-l-lureo-radius-pill transition-[width] duration-lureo-duration-slow ease-lureo-easing-enter', SPLASH)} style={{ width: `${fraction * 100}%` }} />
      </div>
      <p className="text-lureo-14 font-light text-lureo-on-emphasis leading-5">{loadingText}</p>
    </div>
  );
};

export default ProgressBar;
